import { useCallback, type RefObject } from 'react';
import type { ScanAgentApi, ScanAgentCapabilities } from './scan-agent';
import { trackProduct } from './product-tracker';

/** What the scan sheet knows of the scanner's own window: the choice, and whether its settings window is open. */
export interface ScannerWindowState {
  device: string;
  capabilities: ScanAgentCapabilities | null;
  /** The scanner's own window drives the scan (remembered per scanner by Paige Scan). */
  driverWindow: boolean;
  /** "Scanner settings…" is open on this computer: no scan meanwhile. */
  configuring: boolean;
}

export interface ScannerWindowActions {
  setDriverWindow: (on: boolean) => void;
  openSettings: () => Promise<void>;
  resetSettings: () => Promise<void>;
}

type Update = (
  change: Partial<ScannerWindowState> & { problem?: string | null; outcome?: null },
) => void;

/**
 * The two ways past Paige's own choices, for a scanner whose driver has its own window: scan through that window
 * (the choice is kept by Paige Scan for the scanner on this computer), or open its settings window alone, whose
 * setup Paige Scan keeps and puts back before every later scan until Reset.
 */
export function useScannerWindow(
  apiRef: RefObject<ScanAgentApi | null>,
  state: ScannerWindowState,
  update: Update,
): ScannerWindowActions {
  const { device } = state;
  const setDriverWindow = useCallback(
    (on: boolean) => {
      update({ driverWindow: on });
      trackProduct('scan.driver_window', { on });
      void apiRef.current?.rememberWindow(device, on).catch(() => undefined);
    },
    [apiRef, device, update],
  );
  const openSettings = useCallback(async () => {
    const api = apiRef.current;
    if (!api || !device) return;
    update({ configuring: true, problem: null, outcome: null });
    try {
      const id = await api.openSettings(device);
      let poll = await api.settingsStatus(id);
      while (poll.status === 'waiting') poll = await api.settingsStatus(id);
      trackProduct('scan.scanner_settings', { status: poll.status });
      const problem = poll.status === 'failed' ? 'Your scanner did not keep its settings' : null;
      update({ configuring: false, problem, capabilities: await api.capabilities(device) });
    } catch (error) {
      update({
        configuring: false,
        problem: error instanceof Error ? error.message : 'Paige Scan did not answer',
      });
    }
  }, [apiRef, device, update]);
  const resetSettings = useCallback(async () => {
    const api = apiRef.current;
    if (!api || !device) return;
    await api.resetSettings(device).catch(() => undefined);
    update({ capabilities: state.capabilities && { ...state.capabilities, savedSettings: false } });
  }, [apiRef, device, state.capabilities, update]);
  return { setDriverWindow, openSettings, resetSettings };
}
